// A macro step (macros.ts) worked out as masks: the selection rules and plain selection buttons over a page's
// clouds (the editor's scans, or the object page's one overlay), given what the page showed when the run started
// (Ctx). Pure, with no page in sight, so the same code runs a toolbar button there and then, and a macro in the
// macro worker (macroworker.ts) a step at a time while the page shows each step's selection as it lands
// (macrorun.ts). Group edits commit through the page: the run stops there and waits for it (run's 'commit').
import * as sel from './select.ts';
import type { Combine, Cuts, Mask } from './select.ts';
import type { Volume } from '../volumes.ts';
import { RANGE_STEP } from '../lod.ts';
import { pointDensity } from './instances.ts';
import { PHOTO, PHOTO_SEL, type Params, type Step, bindLoop, loopsIn } from './macros.ts';
import { nearEdges } from '../photo/lines3d.ts';
import { type TuneStat, tunePasses } from './tune.ts';
import { components } from '../fit/robust.ts';
import { planarity } from './planarity.ts';
import { type DataExpr, type Measure, MEASURES, evalDataExpr, evalText, measuresIn, parseDataExpr } from './dataexpr.ts';
import { type Curve, type Data, type DataOp, type DataSets, applyData, dataCount, dataMask, dataMembers, falloff, nearness, newData, valueAt } from './datagroups.ts';

/** A cloud's points as the rules see them (world positions); stays put between runs (the worker keeps it). */
export interface Cloud {
  id: string;
  P: Float32Array; N: Float32Array; rgb: Uint8Array;
  /** Per point: source frame (0: none), capture range byte, edge byte; null: not recorded. */
  frame: Uint32Array | null; range: Uint8Array | null; edge: Uint8Array | null;
  /** Its edge bytes have the side (signed Edges): all of them, per point (the overlay, when only some packs' do), or none. */
  edgeSign?: boolean | Uint8Array | null;
  /** Its name, for messages. */
  name?: string;
  /** The overlay: per point, which of its scans it is (ties count per scan) and its piece (→Frames widens to it). */
  si?: Uint16Array | null; piece?: Int32Array | null;
}

/** A cloud as the page has it when the run starts (or after a group edit). */
export interface Held {
  /** Points that may be selected (layers on; on the object page also not cut away or culled). */
  live: Mask;
  /** Unticked in Scans: its selection is parked, and the plain buttons leave it be. */
  off: boolean;
  /** The name the view's cuts know it by (uncut). */
  cut: string;
  /** The points a density measurement is of (null: none of this cloud). */
  keep: Mask | null;
  /** Per scan of the cloud (by si; [0] for a scan), per frame: named points tying it to another frame. */
  ties: Map<number, number>[];
}

export type Plane = [number, number, number, number];
/** A density measurement: per cloud, each point's neighbours within r (0: not measured); range: 2nd to 98th percentile. */
export interface Dens { key: string; r: number; step: number; d: (Uint16Array | null)[]; range: [number, number] }
/** A Points source's masks per cloud (the page works them out: they need its edit state), or why there are none. */
export type Src = { m: (Mask | null)[]; keepDeleted?: boolean } | { err: string };

/** What the photo steps found so far in a run: 3D edges (A, B: 6 each); per cloud, the points on depth edges a photo edge agrees with (Match edges). */
export interface PhotoCtx { edges?: Float32Array; matched?: (Mask | null)[] }

export interface Ctx {
  /** Whose rules: the editor's ('edit') or the object page's, which differ in a few messages and checks. */
  host: 'edit' | 'objects';
  /** Per cloud, in the order of the clouds. */
  at: Held[];
  vols: Volume[];
  cam: { e: number[]; f: number[]; ortho: boolean };
  /** Backfaces can't be selected: the cull threshold (null: no culling, or live has it already). */
  cull: number | null;
  /** The view's cuts, for All shown (null: live has them already). */
  cuts: Cuts | null;
  floorZ: number;
  /** The plane Band's Fit found last. */
  plane: Plane | null;
  /** Points sources the steps use (Points, Group), by their key ('floor', 'group:g', …). */
  src: Record<string, Src>;
  /** What the points measured for density are (with r, a measurement's key), and the page's last measurement. */
  densKey: string;
  dens?: Dens | null;
  photo?: PhotoCtx;
  /** The data groups (datagroups.ts) as the run has them; none: no data groups yet. */
  data?: DataSets;
  /** Per cloud, the points' weights (a data step's weight source; the editor's only). */
  weights?: (Float32Array | null)[];
}

/** A step's outcome: the selection after it, and what the page records and says. */
export interface Out {
  /** false: it couldn't (say says why) and sel is as it was. */
  ok: boolean;
  sel: Mask[];
  /** The selection changed. */
  changed: boolean;
  /** How the selection rule (the editor's record of what made it) goes on: a rule combined with mode, the old one wrapped, or none. */
  rule?: { leaf: Record<string, unknown>; mode: Combine } | { wrap: Record<string, unknown> } | { clear: true };
  say?: string; warn?: boolean;
  plane?: Plane;
  /** A density step: the measurement's radius and spread; dens: the measurement, when this step made it. */
  densRange?: { r: number; range: [number, number] };
  dens?: Dens;
  /** An Auto tune step: how it went, and the best it found (as steps). */
  tune?: TuneStat;
  /** A data step: the data groups it changed (null: deleted). */
  data?: Record<string, Data | null>;
}

/**
 * What a run keeps between steps: the last density measurement; macro: a Band on the fitted plane fits it there and
 * then; finish: the page said to finish an Auto tune with what it has found; part: while the selection is sel (that
 * very array) it is a For each time round's part, its points per cloud (idx), so a step may go over those alone.
 */
export interface Mem { dens?: Dens | null; macro?: boolean; finish?: boolean; part?: { sel: Mask[]; idx: (Uint32Array | null)[] }; slots?: Map<string, Mask[]> }

const zeros = (n: number) => new Uint8Array(n);
const anySel = (s: Mask[]) => s.some(m => m.some(x => x));
export const holds = anySel;

/** Point density over every cloud's keep points, as one cloud (they overlap: counted against each other). */
export function measure(clouds: Cloud[], at: Held[], r: number, key: string): Dens | null {
  let total = 0;
  for (const h of at) if (h.keep) total += sel.count(h.keep);
  if (!total) return null;
  const xyz = new Float32Array(3 * total);
  let o = 0;
  clouds.forEach((c, k) => { const keep = at[k].keep; if (keep) for (let i = 0; i < keep.length; i++) if (keep[i]) { xyz[o++] = c.P[3 * i]; xyz[o++] = c.P[3 * i + 1]; xyz[o++] = c.P[3 * i + 2]; } });
  // neighbours counted among at most ~1.5M of them (every step-th), scaled up: the counts hardly change
  const step = Math.max(1, Math.ceil(total / 1.5e6)), sample = new Float32Array(3 * Math.ceil(total / step));
  for (let i = 0, j = 0; i < total; i += step, j++) sample.set(xyz.subarray(3 * i, 3 * i + 3), 3 * j);
  return densFrom(at, pointDensity(sample, step, xyz, r), r, step, key);
}

/** A measurement from every keep point's density (in cloud then point order): per cloud, and the 2nd to 98th percentile. */
export function densFrom(at: Held[], all: Uint16Array, r: number, step: number, key: string): Dens {
  const total = all.length, k = Math.max(1, Math.floor(total / 200000)), q = Uint16Array.from({ length: Math.ceil(total / k) }, (_, i) => all[i * k]).sort();
  const lo = q[Math.floor(0.02 * (q.length - 1))] ?? 1, hi = q[Math.floor(0.98 * (q.length - 1))] ?? 1;
  let a = 0;
  const d = at.map(h => {
    if (!h.keep) return null;
    const x = new Uint16Array(h.keep.length);
    for (let v = 0; v < x.length; v++) if (h.keep[v]) x[v] = all[a++];
    return x;
  });
  return { key, r, step, d, range: [Math.max(1, lo), Math.max(lo + 1, hi)] };
}

/** The key of a density measurement at r of ctx's points. */
export const densKey = (ctx: Ctx, r: number) => `${r}|${ctx.densKey}`;

/** A Colour step's band: s and v put in order; hue kept as given (from > to goes round through red). */
export function colourBand(p: Params): sel.HsvBand {
  return { h0: +p.h0, h1: +p.h1, s0: Math.min(+p.s0, +p.s1), s1: Math.max(+p.s0, +p.s1), v0: Math.min(+p.v0, +p.v1), v1: Math.max(+p.v0, +p.v1) };
}

/** Fit a plane to the selected points of every shown cloud. */
export function fitPlane(clouds: Cloud[], at: Held[], cur: Mask[]): Plane | null {
  const pts: number[] = [];
  clouds.forEach((c, k) => { if (!at[k].off) for (let i = 0; i < cur[k].length; i++) if (cur[k][i]) pts.push(c.P[3 * i], c.P[3 * i + 1], c.P[3 * i + 2]); });
  return sel.fitPlane(new Float32Array(pts), new Uint8Array(pts.length / 3).fill(1));
}
export const planeSay = (p: Plane) => `plane n=(${p.slice(0, 3).map(x => x.toFixed(3)).join(', ')}), ${(-p[3]).toFixed(3)} m from the origin`;

/**
 * Step s on the selection cur (a mask per cloud, parked ones too): the selection after it. mem keeps a density
 * measurement from one step to the next. Throws on a Group edit or Macro step (the run handles those).
 */
export function evalStep(clouds: Cloud[], cur: Mask[], ctx: Ctx, s: Step, mem: Mem = {}): Out {
  const p = s.p, at = ctx.at, objs = ctx.host === 'objects';
  const fail = (say: string): Out => ({ ok: false, sel: cur, changed: false, say });
  const same = (say?: string, warn?: boolean): Out => ({ ok: true, sel: cur, changed: false, say, warn });

  /** A mask per cloud (null: nothing there), the other way round with p.inv, combined into the selection with mode, kept to live (and facing) points. */
  const inv = p.inv === true;
  const masks = (f: (c: Cloud, k: number) => Mask | null, leaf: Record<string, unknown>, keepDeleted = false, extra: Partial<Out> = {}): Out => {
    let mode = s.mode ?? 'replace';
    if (mode === 'inter' && !anySel(cur)) mode = 'replace';   // intersecting nothing: start a new selection
    if (ctx.cull !== null) leaf = { ...leaf, cull: true, cullT: ctx.cull };
    if (inv) leaf = { t: 'not', of: leaf };
    const out = clouds.map((c, k) => {
      const m = f(c, k) ?? zeros(cur[k].length);
      if (inv) for (let i = 0; i < m.length; i++) m[i] ^= 1;   // the points it wouldn't select (kept to live ones below)
      if (!keepDeleted) { const live = at[k].live; for (let i = 0; i < m.length; i++) m[i] &= live[i]; }
      if (ctx.cull !== null) { const fc = sel.facing(c.P, c.N, ctx.cam.e, ctx.cam.f, ctx.cam.ortho, ctx.cull); for (let i = 0; i < m.length; i++) m[i] &= fc[i]; }
      return sel.combine(mode, cur[k], m);
    });
    return { ok: true, sel: out, changed: true, rule: { leaf, mode }, ...extra };
  };
  const lohi = () => [Math.min(+p.lo, +p.hi), Math.max(+p.lo, +p.hi)] as const;
  const shownSel = () => clouds.some((_, k) => !at[k].off && cur[k].some(x => x));

  switch (s.t) {
    case 'all': return masks((c, k) => (ctx.cuts ? sel.uncut(c.P, ctx.cuts, at[k].cut) : new Uint8Array(c.P.length / 3).fill(1)), { t: 'all' });
    case 'box': {
      const id = String(p.vol), use = id === '*' ? ctx.vols.filter(v => v.on) : ctx.vols.filter(v => v.id === id);
      if (!use.length) return fail(id === '*' ? `no volume: draw a box in Volumes${objs ? ' (in the editor)' : ''} first` : `no volume ${id}`);
      return masks(c => sel.box(c.P, use), { t: 'box', boxes: use.map(v => ({ center: v.center, size: v.size, ypr: v.ypr })) });
    }
    case 'normal': {
      const ax = String(p.axis), deg = +p.deg || 20;
      return masks(c => {
        if (ax !== 'h') return sel.normalCone(c.N, ax === 'x' ? [1, 0, 0] : ax === 'y' ? [0, 1, 0] : [0, 0, 1], deg);
        const m = sel.normalCone(c.N, [0, 0, 1], 90 - deg);   // horizontal: more than 90-deg from vertical
        for (let i = 0; i < m.length; i++) m[i] = c.N[3 * i] === c.N[3 * i] ? m[i] ^ 1 : 0;
        return m;
      }, { t: 'normal', axis: ax, deg });
    }
    case 'band': {
      let plane: Plane | null = p.plane === 'fit' ? ctx.plane : [0, 0, 1, -ctx.floorZ];
      let extra: Partial<Out> = {};
      if (p.plane === 'fit' && mem.macro) {   // a macro's band fits the selection as it is at that step
        plane = fitPlane(clouds, at, cur);
        if (!plane) return fail('select at least 3 points of the plane first');
        extra = { plane, say: planeSay(plane) };
      }
      if (!plane) return fail('fit a plane first (select its points, press Fit)');
      const [lo, hi] = lohi(), pl = plane;
      return masks(c => sel.band(c.P, pl, lo, hi), { t: 'band', plane, lo: +p.lo, hi: +p.hi }, false, extra);
    }
    case 'distance': {
      const [lo, hi] = lohi();
      if (!clouds.some(c => c.range)) return fail('no capture ranges in these scans (rebuild them with prep/build_tiles.py)');
      return masks(c => (c.range ? sel.distance(c.range, RANGE_STEP, lo, hi) : null), { t: 'distance', lo, hi });
    }
    case 'edges': {   // Sobel strength, either side or signed (only where the bytes have the side), Canny
      const [lo, hi] = lohi(), canny = p.canny === 'on' ? true : p.canny === 'off' ? false : null, signed = p.side === 'signed';
      const all = clouds.filter(c => c.edge?.some(e => e & 1)), signs = (c: Cloud) => c.edgeSign === true || (c.edgeSign instanceof Uint8Array && c.edgeSign.some(x => x));
      if (!all.length) return fail('no image edges in these scans (run prep/edges.py --scene, then Publish)');
      if (signed && !all.some(signs)) return fail('no signed edges in these scans (run prep/edges.py --scene, then Publish)');
      const skip = signed ? all.filter(c => c.edgeSign !== true) : [];
      return masks(c => {
        if (!c.edge || (signed && !signs(c))) return null;
        const m = sel.edges(c.edge, lo, hi, canny, signed), S = c.edgeSign;
        if (signed && S instanceof Uint8Array) for (let i = 0; i < m.length; i++) m[i] &= S[i];
        return m;
      }, { t: 'edges', lo, hi, canny, signed }, false, skip.length ? {
        say: objs ? 'signed edges: skipped scans with no side yet (run prep/edges.py --scene, then Publish)'
          : `signed edges: skipped ${skip.map(c => c.name ?? c.id).join(', ')} (no side yet: run prep/edges.py --scene, then Publish)`,
      } : {});
    }
    case 'colour': { const b = colourBand(p); return masks(c => sel.colour(c.rgb, b), { t: 'colour', ...b }); }
    case 'view': {   // the angle between the camera ray (seen backwards) and the normal: within spread of at
      const at = +p.at, spread = +p.spread, { e, f, ortho } = ctx.cam;
      return masks(c => sel.viewCone(c.P, c.N, e, f, ortho, at, spread), { t: 'view', at, spread, eye: [...e], fwd: [...f], ortho });
    }
    case 'density': {
      const r = +p.r, [lo, hi] = lohi(), key = densKey(ctx, r);
      let d = ctx.dens?.key === key ? ctx.dens : mem.dens?.key === key ? mem.dens : null, fresh = false;
      if (!d) { d = measure(clouds, at, r, key); fresh = !!d; }
      if (!d) return fail('no points to measure');
      mem.dens = d;
      const res = d;
      return masks((_, k) => {
        const x = res.d[k];
        if (!x) return null;
        const m = new Uint8Array(x.length);
        for (let i = 0; i < x.length; i++) m[i] = x[i] && x[i] >= lo && x[i] <= hi ? 1 : 0;
        return m;
      }, { t: 'density', r, lo, hi }, false, { densRange: { r, range: res.range }, ...(fresh ? { dens: res } : {}) });
    }
    case 'matchEdges': {
      const M = ctx.photo?.matched;
      if (!M) return fail('no matched edges (the step finds them on the page)');
      let n = 0;
      const out = masks((_, k) => { const m = M[k]?.slice() ?? null; if (m) { const live = at[k].live; for (let i = 0; i < m.length; i++) n += m[i] & live[i]; } return m; }, { t: 'matchEdges', kind: p.kind, tol: +p.tol });
      return { ...out, say: `${n.toLocaleString()} points on them` };
    }
    case 'edges3d': {
      const E = ctx.photo?.edges, r = +p.r;
      if (!E?.length) return fail('no 3D edges (the step finds them on the page)');
      let n = 0;
      const out = masks(c => { const m = nearEdges(c.P, E, r); for (const x of m) n += x; return m; }, { t: 'edges3d', r, edges: E.length / 6 });
      return { ...out, say: `${n.toLocaleString()} points within ${r} m of them` };
    }
    case 'frames': {
      const a = +p.a, b = +p.b;
      if (!a || !b) return fail('enter a frame range');
      if (objs && !clouds.some(c => c.frame?.some(f => f))) return fail('no frame ids in these scans');
      const r: [number, number] = [Math.min(a, b), Math.max(a, b)];
      return masks(c => (c.frame ? sel.frames(c.frame, [r]) : null), { t: 'frames', ranges: [r] });
    }
    case 'few': {
      const n = Math.max(1, Math.round(+p.n || 0));
      if (objs && !clouds.some(c => c.frame?.some(f => f))) return fail('no frame ids in these scans');
      return masks((c, k) => {
        const F = c.frame, ties = at[k].ties, si = c.si;
        if (!F) return null;
        if (!si) return sel.fewPoints(F, ties[0] ?? new Map(), n);
        const m = new Uint8Array(F.length);
        for (let i = 0; i < F.length; i++) m[i] = F[i] && (ties[si[i]]?.get(F[i]) ?? 0) < n ? 1 : 0;
        return m;
      }, { t: 'few points', n });
    }
    case 'points': case 'group': {
      const k = s.t === 'group' ? `group:${p.name}` : String(p.src), src = k.startsWith('data:') ? dataSrc(ctx, k.slice(5)) : ctx.src[k];
      if (!src) return fail(`no such points: ${k}`);
      if ('err' in src) return fail(src.err);
      const i = k.indexOf(':'), kind = i < 0 ? k : k.slice(0, i), arg = i < 0 ? '' : k.slice(i + 1);
      return masks((_, j) => (src.m[j] ? src.m[j]!.slice() : null), { t: kind, ...(arg ? { id: arg } : {}) }, !!src.keepDeleted);
    }
    case 'invert':
      return { ok: true, changed: true, rule: { wrap: { t: 'invert' } }, sel: cur.map((m, k) => {
        if (at[k].off) return m;   // unticked: keeps its parked selection
        const live = at[k].live, out = new Uint8Array(m.length);
        for (let i = 0; i < m.length; i++) out[i] = live[i] & (m[i] ^ 1);
        return out;
      }) };
    case 'clear': return { ok: true, changed: true, rule: { clear: true }, sel: cur.map(m => zeros(m.length)) };
    case 'widenFrames': case 'widenScan': {
      if (!shownSel()) return same('select some points first');
      const to = s.t === 'widenScan' ? 'scan' : 'frames';
      let warn = '', noFrames = 0;
      const out = cur.map((m0, k) => {
        if (at[k].off || !m0.some(x => x)) return m0;
        const c = clouds[k], live = at[k].live;
        let w: Mask;
        if (to === 'scan') w = c.si ? byKey(m0, c.si) : new Uint8Array(m0.length).fill(1);
        else if (c.piece) {
          if (!c.frame?.some(f => f)) warn = 'no frame ids in these scans: widened to the scans\' pieces';
          w = byKey(m0, c.piece);
        } else if (c.frame) w = sel.widenToFrames(m0, c.frame);
        else { noFrames++; w = m0; }
        const m = new Uint8Array(m0.length);
        for (let i = 0; i < m.length; i++) m[i] = m0[i] | (w[i] & live[i]);
        return m;
      });
      if (noFrames) warn = `${noFrames === 1 ? 'a scan has' : `${noFrames} scans have`} no frame ids: left as it was`;
      return { ok: true, changed: true, rule: { wrap: { t: 'widen', to } }, sel: out, say: warn || undefined, warn: !!warn };
    }
    case 'erode': case 'dilate': {
      if (!shownSel()) return same('select some points first');
      const kind = s.t, r = Math.max(0.005, +p.r || 0.05), use = clouds.map((_, k) => k).filter(k => !at[k].off);
      const near = use.map(k => {
        const m = cur[k], live = at[k].live, on = new Uint8Array(m.length), off = new Uint8Array(m.length);
        for (let i = 0; i < m.length; i++) { on[i] = live[i] & m[i]; off[i] = live[i] & (m[i] ^ 1); }   // selected but hidden: stays as it is
        return { P: clouds[k].P, src: kind === 'dilate' ? on : off, dst: kind === 'dilate' ? off : on };
      });
      const hit = sel.near(near, r), out = cur.slice();
      let n = 0;
      use.forEach((k, j) => {
        const m = cur[k], h = hit[j], o = new Uint8Array(m.length);
        for (let i = 0; i < m.length; i++) { o[i] = kind === 'dilate' ? m[i] | h[i] : m[i] & (h[i] ^ 1); n += h[i]; }
        out[k] = o;
      });
      if (!n) return same(kind === 'dilate' ? `nothing shown within ${r} m of the selection` : `nothing shown unselected within ${r} m of the selection`);
      return { ok: true, changed: true, rule: { wrap: { t: kind, r } }, sel: out, say: `${kind === 'dilate' ? 'added' : 'dropped'} ${n.toLocaleString()} points` };
    }
    case 'dataBand': {
      const name = String(p.name), d = ctx.data?.[name], [lo, hi] = lohi(), nulls = p.nulls === true;
      if (!d) return fail(`no data group "${name}"`);
      const inBand = (x: number) => x >= lo && x <= hi, outside = nulls && inBand(d.dflt);
      return masks((_, k) => {
        const a = d.v[k], n = cur[k].length;
        if (!a) return outside ? new Uint8Array(n).fill(1) : null;
        const m = new Uint8Array(n);
        for (let i = 0; i < n; i++) { const x = a[i]; m[i] = x === x ? (inBand(x) ? 1 : 0) : outside ? 1 : 0; }
        return m;
      }, { t: 'data band', name, lo, hi, nulls });
    }
    // a For each time round's points are held as they are and put back as they are, so a graph run each time round
    // still works on just those points (dataStep's only)
    case 'keep': (mem.slots ??= new Map()).set(String(p.slot), cur === mem.part?.sel ? cur : cur.map(m => m.slice())); return same();
    case 'recall': {   // as it was held: not kept to live or facing points again (it was when it was made)
      const m = mem.slots?.get(String(p.slot));
      if (!m) return fail(`no node output ${p.slot} held yet`);
      const mode = s.mode ?? 'replace';
      return { ok: true, changed: true, rule: { leaf: { t: 'node', id: p.slot }, mode }, sel: mode === 'replace' && m === mem.part?.sel ? m : cur.map((c, k) => sel.combine(mode, c, m[k])) };
    }
    case 'dataCopy': {
      const of = String(p.of), d = ctx.data?.[of];
      if (!d) return fail(`no data group "${of}"`);
      return { ok: true, sel: cur, changed: false, data: { [String(p.name)]: { dflt: d.dflt, v: d.v.slice() } } };   // the arrays shared: a change copies the one it changes (applyData)
    }
    case 'dataNew': case 'dataOp': case 'dataEdit': return dataStep(clouds, cur, ctx, s, mem);
    case 'dataEval': return dataEvalStep(clouds, cur, ctx, s, mem);
    case 'planeDetect': return dataStep(clouds, cur, ctx, { t: 'dataNew', p: { ...s.p, src: 'planar' } }, mem);
    case 'groupEdit': case 'groupSave': case 'plane': case 'weight': case 'weightBlur': case 'macro': case 'blobs': case 'features': case 'calibrate': case 'alignCameras': case 'mesh': case 'tune': throw new Error(`a ${s.t} step runs through the page (run)`);
  }
}

/**
 * The Points sources (ctx.src keys) steps name: Points, Group, a data step's nearness (not the selection or data
 * groups: the run has those itself); none for an input that is a For each's variable (the run asks when it knows).
 */
export function srcKeys(steps: Step[]): string[] {
  const out = new Set<string>(), add = (k: string) => { if (k !== 'sel' && !k.startsWith('data:') && !k.includes('<#')) out.add(k); };
  for (const s of steps) {
    if (s.t === 'points' || s.t === 'group') add(s.t === 'group' ? `group:${s.p.name}` : String(s.p.src));
    if ((s.t === 'dataNew' || s.t === 'dataOp') && s.p.src === 'near') add(String(s.p.from));
  }
  return [...out];
}

/** A data group's points as a Points source. */
function dataSrc(ctx: Ctx, name: string): Src | undefined {
  const d = ctx.data?.[name];
  return d ? { m: dataMask(d) } : { err: `no data group "${name}"` };
}

/** The radius a data step measures point density at (null: it doesn't; Data eval: when it reads density5). */
export const dataDensR = (s: Step) => {
  if (s.t === 'dataEval') { const q = parseDataExpr(evalText(String(s.p.name), String(s.p.op), String(s.p.expr))); return typeof q !== 'string' && q.uses.includes('density5') ? 0.05 : null; }
  return (s.t === 'dataNew' || s.t === 'dataOp') && s.p.src === 'density' ? +s.p.dr : null;
};

/**
 * The measures (dataexpr.ts MEASURES) q reads where sets has no group of the name: the points' position, or a data
 * step's source (mem keeps a density measured); or why one can't be had.
 */
export function measuresFor(clouds: Cloud[], ctx: Ctx, q: DataExpr, sets: DataSets, mem: Mem): Record<string, Measure> | string {
  const out: Record<string, Measure> = {};
  for (const u of measuresIn(q, sets)) {
    const m = MEASURES[u], j = 'xyz'.indexOf(u);
    if (!m.src) { out[u] = (k, i) => clouds[k].P[3 * i + j]; continue; }
    const f = sourceOf(clouds, clouds.map(c => zeros(c.P.length / 3)), ctx, m.src, clouds.map(() => null), mem);
    if (typeof f === 'string') return `${u}: ${f}`;
    out[u] = f;
  }
  return out;
}

/** Data eval: `name op= expr` (dataexpr.ts) over ctx.data and the measures; the selected (shown, live) points where it reads no group. */
function dataEvalStep(clouds: Cloud[], cur: Mask[], ctx: Ctx, s: Step, mem: Mem): Out {
  const p = s.p, name = String(p.name), sets = ctx.data ?? {};
  const fail = (say: string): Out => ({ ok: false, sel: cur, changed: false, say });
  const q = parseDataExpr(evalText(name, String(p.op), String(p.expr)));
  if (typeof q === 'string') return fail(q);
  const ms = measuresFor(clouds, ctx, q, sets, mem);
  if (typeof ms === 'string') return fail(ms);
  const picked = cur.map((m, k) => {
    if (ctx.at[k].off) return null;
    const live = ctx.at[k].live, o = new Uint8Array(m.length);
    for (let i = 0; i < m.length; i++) o[i] = m[i] & live[i];
    return o;
  });
  const g = evalDataExpr(q, sets, clouds.length, picked, ms);
  if (typeof g === 'string') return fail(g);
  const n = g.v.reduce((a, v) => a + dataCount(v), 0);
  return { ok: true, sel: cur, changed: false, data: { [name]: g }, say: `data group "${name}": ${n.toLocaleString()} points` };
}

/**
 * A data step (Data group, Data math, Data edit) on ctx.data: the selection stays; out.data has the group it made,
 * changed or deleted. Its source values (srcOf) are per point, NaN where unknown.
 */
function dataStep(clouds: Cloud[], cur: Mask[], ctx: Ctx, s: Step, mem: Mem): Out {
  const p = s.p, at = ctx.at, name = String(p.name), d = ctx.data?.[name];
  const fail = (say: string): Out => ({ ok: false, sel: cur, changed: false, say });
  const done = (g: Data | null, say: string): Out => ({ ok: true, sel: cur, changed: false, data: { [name]: g }, say });
  const only = mem.part?.sel === cur ? mem.part.idx : null;
  if (only && s.t === 'dataOp' && d && p.sel === true && p.src !== 'near' && p.src !== 'planar') {   // a For each time round's points: those alone, not every point
    const ix = only.map((x, k) => { if (!x || at[k].off) return null; const live = at[k].live; return x.filter(i => live[i]); });
    if (!ix.some(x => x?.length)) return fail('select some points first');
    const src = sourceOf(clouds, cur, ctx, p, clouds.map(() => null), mem);
    if (typeof src === 'string') return fail(src);
    const what = describeSrc(p), r = applyData(d, p.op as DataOp, src, null, ix);
    if (!r) return { ok: true, sel: cur, changed: false, say: `no value changed (${what})` };
    return done(r.d, `${r.n.toLocaleString()} values changed by ${what}`);
  }
  if (s.t === 'dataEdit' && p.act === 'delete') return d ? done(null, `data group "${name}" deleted`) : { ok: true, sel: cur, changed: false, say: `no data group "${name}"` };
  // the selected points that may be (shown, live)
  const picked = cur.map((m, k) => {
    if (at[k].off) return null;
    const live = at[k].live, o = new Uint8Array(m.length);
    let any = 0;
    for (let i = 0; i < m.length; i++) any |= o[i] = m[i] & live[i];
    return any ? o : null;
  });
  const none = !picked.some(Boolean);
  if (s.t === 'dataEdit') {
    if (none) return fail('select some points first');
    const add = p.act === 'add', g = d ?? { dflt: 0, v: clouds.map(() => null) };
    const r = dataMembers(g, picked, add);
    if (!r) return { ok: true, sel: cur, changed: false, say: add ? 'they are in it already' : 'none of them is in it' };
    return done(r.d, `${add ? 'added' : 'took out'} ${r.n.toLocaleString()} points`);
  }
  if (s.t === 'dataNew' && none) return fail('select some points first');
  if (s.t === 'dataOp' && !d) return fail(`no data group "${name}" (make it with a Data group step)`);
  // the points that take a value: the selection (a new group; by nearness, every shown point that may be in reach
  // too), or the group's (∩ the selection)
  const sel = p.sel === true, reach = s.t === 'dataNew' && p.src === 'near';
  const dst = reach ? at.map(a => (a.off ? null : a.live)) : s.t === 'dataNew' ? picked : d!.v.map((a, k) => {
    if (!a || (sel && !picked[k])) return null;
    const o = new Uint8Array(a.length);
    for (let i = 0; i < a.length; i++) o[i] = a[i] === a[i] && (!sel || picked[k]![i]) ? 1 : 0;
    return o;
  });
  const src = sourceOf(clouds, cur, ctx, p, dst, mem, reach);
  if (typeof src === 'string') return fail(src);
  const what = describeSrc(p);
  if (reach) {
    // the selection and the points in reach of the source; a selected point out of reach as far
    const far = falloff(Infinity, +p.r, p.curve as Curve);
    const g = newData(clouds.map((c, k) => {
      if (!dst[k]) return null;
      const m = picked[k], o = new Uint8Array(dst[k]!.length);
      for (let i = 0; i < o.length; i++) o[i] = m?.[i] || src(k, i) === src(k, i) ? 1 : 0;
      return o;
    }), +p.dflt, (k, i) => { const x = src(k, i); return x === x ? x : far; });
    const n = g.v.reduce((a, v) => a + dataCount(v), 0);
    return done(g, `data group "${name}": ${n.toLocaleString()} points from ${what}`);
  }
  if (s.t === 'dataNew') {
    const g = newData(picked, +p.dflt, src);
    const n = g.v.reduce((a, v) => a + dataCount(v), 0);
    return done(g, `data group "${name}": ${n.toLocaleString()} points from ${what}`);
  }
  const r = applyData(d!, p.op as DataOp, src, sel ? (k, i) => !!picked[k]?.[i] : null);
  if (!r) return { ok: true, sel: cur, changed: false, say: `no value changed (${what})` };
  return done(r.d, `${r.n.toLocaleString()} values changed by ${what}`);
}

const describeSrc = (p: Params) => {
  switch (p.src) {
    case 'const': return String(p.x);
    case 'density': return `density within ${+p.dr * 100} cm`;
    case 'edges': return `${p.side === 'signed' ? 'signed ' : ''}edge strength`;
    case 'data': return `data group "${p.of}"`;
    case 'near': return `nearness to ${p.from === 'sel' ? 'the selection' : p.from}${String(p.from).startsWith('data:') ? ' (by its values)' : ''} within ${p.r} m (${p.curve})`;
    case 'planar': return `plane likelihood within ${p.r} m (flat to ${+(+p.tol * 100).toFixed(1)} cm, normals to ${p.deg}°)`;
    default: return String(p.src);
  }
};

/**
 * A data step's source: per point of a cloud its value (NaN: unknown), or why there is none. dst: the points that
 * want one. reach: a nearness is unknown (NaN) beyond r of every source, not the curve's value there.
 */
function sourceOf(clouds: Cloud[], cur: Mask[], ctx: Ctx, p: Params, dst: (Uint8Array | null)[], mem: Mem, reach = false): ((k: number, i: number) => number) | string {
  switch (p.src) {
    case 'const': { const x = +p.x; return () => x; }
    case 'height': { const z0 = ctx.floorZ; return (k, i) => clouds[k].P[3 * i + 2] - z0; }
    case 'range': return (k, i) => { const R = clouds[k].range, x = R ? R[i] : 0; return x ? x * RANGE_STEP : NaN; };
    case 'edges': {
      const signed = p.side === 'signed';
      if (!clouds.some(c => c.edge?.some(e => e & 1))) return 'no image edges in these scans (run prep/edges.py --scene, then Publish)';
      return (k, i) => {
        const c = clouds[k], E = c.edge, e = E ? E[i] : 0, S = c.edgeSign;
        if (!(e & 1) || (signed && !(S === true || (S instanceof Uint8Array && S[i])))) return NaN;
        return (signed ? sel.edgeSigned(e) : e >> 3) / 31;
      };
    }
    case 'weight': {
      const W = ctx.weights;
      if (!W) return 'point weights are the scan editor\'s';
      return (k, i) => W[k]?.[i] ?? NaN;
    }
    case 'density': {
      const r = +p.dr, key = densKey(ctx, r);
      let dd = ctx.dens?.key === key ? ctx.dens : mem.dens?.key === key ? mem.dens : null;
      if (!dd) dd = measure(clouds, ctx.at, r, key);
      if (!dd) return 'no points to measure';
      mem.dens = dd;
      const D = dd.d;
      return (k, i) => { const x = D[k]?.[i] ?? 0; return x ? x : NaN; };
    }
    case 'data': {
      const o = ctx.data?.[String(p.of)];
      if (!o) return `no data group "${p.of}"`;
      return (k, i) => valueAt(o, k, i);
    }
    case 'near': {
      const from = String(p.from), r = +p.r, curve = p.curve as Curve;
      let src: (Uint8Array | null)[], w: (Float32Array | null)[] | null = null;
      if (from === 'sel') src = cur;
      // a data group's points, each counting by its value (to at most 1; none at 0 or less)
      else if (from.startsWith('data:')) { const o = ctx.data?.[from.slice(5)]; if (!o) return `no data group "${from.slice(5)}"`; src = dataMask(o); w = o.v; }
      else { const x = ctx.src[from]; if (!x) return `no such points: ${from}`; if ('err' in x) return x.err; src = x.m; }
      // shown, live source points (as Dilate takes them), every cloud's together
      const near = nearness(clouds.map((c, k) => {
        const m = src[k], live = ctx.at[k].live;
        let s: Uint8Array | null = null;
        if (m && !ctx.at[k].off) { s = new Uint8Array(m.length); for (let i = 0; i < m.length; i++) s[i] = m[i] & live[i]; }
        return { P: c.P, src: s, dst: dst[k], w: w?.[k] };
      }), r, curve);
      if (!src.some((m, k) => m && !ctx.at[k].off && m.some((x, i) => x && ctx.at[k].live[i]))) return `no points shown in ${from === 'sel' ? 'the selection' : from}`;
      if (reach) return (k, i) => near[k][i];
      const far = falloff(Infinity, r, curve);
      return (k, i) => { const x = near[k][i]; return x === x ? x : far; };
    }
    case 'planar': {
      // every shown, live point is a neighbour (scans overlap); the points wanting a value are dst
      const out = planarity(clouds.map((c, k) => ({ P: c.P, N: c.N, pool: ctx.at[k].off ? null : ctx.at[k].live, dst: dst[k] })), +p.r, +p.tol, +p.deg);
      return (k, i) => out[k]?.[i] ?? NaN;
    }
    default: return `unknown source ${p.src}`;
  }
}

/** m widened to every point whose key a selected point has. */
function byKey(m: Mask, key: ArrayLike<number>): Mask {
  const hit = new Set<number>();
  for (let i = 0; i < m.length; i++) if (m[i]) hit.add(key[i]);
  const out = new Uint8Array(m.length);
  for (let i = 0; i < m.length; i++) out[i] = hit.has(key[i]) ? 1 : 0;
  return out;
}

// ── for the page: what goes to the worker, and when it has to go again ──
/** Copies of arrays that may be views on a bigger buffer (wasm memory): just the view goes to the worker. */
export function own<T extends ArrayBufferView | null | undefined>(a: T): T {
  return (a && (a.byteOffset || a.byteLength !== a.buffer.byteLength || !(a.buffer instanceof ArrayBuffer)) ? (a as unknown as { slice(): T }).slice() : a) as T;
}

const ids = new WeakMap<object, number>();
let nextId = 0;
/** A number for an object, the same while it lives (whether the page has new arrays since). */
export function idOf(o: object) { let i = ids.get(o); if (i === undefined) ids.set(o, (i = ++nextId)); return i; }

/** A cheap fingerprint of where points are (every 301st coordinate summed). */
export function where(P: Float32Array) { let s = 0; for (let i = 0; i < P.length; i += 301) s += P[i]; return `${P.length}:${s}`; }

/**
 * What a run tells the page: a step done, a group edit for it to commit (then resume), a density measurement it
 * could make faster (its worker pool; resume with it in ctx.dens, or without: the run measures it), a step that couldn't.
 */
export type Ev = ({ k: 'step'; i: number; out: Out } | { k: 'measure'; i: number; r: number }
  /** s: the step with a For each's number put in, when it has one (the page's steps have its token). */
  | { k: 'commit'; i: number; s?: Step } | { k: 'photo'; i: number; s?: Step }
  /** The Points sources a step with a For each's number names, for the page to work out (resume with them in ctx.src). */
  | { k: 'src'; i: number; keys: string[] }
  | { k: 'fail'; i: number; why: string }
  /** An Auto tune step under way: how it's going, and the best selection so far when that changed (null: as it was). */
  | { k: 'tune'; i: number; stat: TuneStat; out: Out | null })
  /** Inside For each steps: where the run is (Where). */
  & { at?: Where };

/**
 * Where a run is inside For each steps: each one's time round, outer first ("blob 3 of 120", "2 of 5"), and how far
 * through the run's steps it has got (pos: steps, a time round counting as its share of its For each's body).
 */
export interface Where { rounds: string[]; pos: number }

/**
 * A For each step under way: its place in the run, how many steps its body is, its time round (from 0) of n, said
 * as text; done: its last event (the rounds over: not said, it counts as through its body).
 */
interface Round { i: number; body: number; b: number; n: number; text: string; done?: boolean }

/** Where step i is, inside the rounds under way (outer first). */
function whereIn(rounds: Round[], i: number): Where {
  let pos = i;
  for (let j = rounds.length - 1; j >= 0; j--) {   // inner first: a share of its body, then of the one around it
    const r = rounds[j], f = r.body ? Math.min(1, Math.max(0, (pos - r.i - 1) / r.body)) : 0;
    pos = r.i + 1 + ((r.b + f) / Math.max(1, r.n)) * r.body;
  }
  return { rounds: rounds.filter(r => !r.done).map(r => r.text), pos };
}

/** The keys an Auto tune step's lists go by in ctx.src (i: the step's place in the run). */
export const tuneKey = (i: number, list: 'req' | 'deny') => `tune:${i}:${list}`;
/**
 * After a commit: the page's state as the edit left it (layers, groups); after a measure, the measurement; after a
 * photo step, what it found (ctx.photo), what to say, or why it couldn't (fail); after a src, the sources (ctx.src,
 * added to those the run has); reload: the clouds moved (the worker swapped them in).
 */
export type Resume = { ctx?: Partial<Ctx>; say?: string; warn?: boolean; fail?: string; reload?: boolean } | undefined;

/** A part of the selection a For each step goes over: its points, each a cloud (from) and a point in it (idx). */
export interface Part { from: Int32Array; idx: Uint32Array }

/** A part's points per cloud (null: none there): from is in cloud order, so each cloud's are a run of it. */
function byCloud(part: Part, nc: number): (Uint32Array | null)[] {
  const out: (Uint32Array | null)[] = Array(nc).fill(null);
  for (let j = 0; j < part.idx.length; ) {
    const k = part.from[j];
    let e = j;
    while (e < part.idx.length && part.from[e] === k) e++;
    out[k] = part.idx.subarray(j, e);
    j = e;
  }
  return out;
}

/** A part as a mask per cloud (the rest empty); into: or'd into those masks instead. */
function maskOf(part: Part, cur: Mask[], into?: Mask[]): Mask[] {
  const out = into ?? cur.map(m => new Uint8Array(m.length));
  for (let j = 0; j < part.idx.length; j++) out[part.from[j]][part.idx[j]] = 1;
  return out;
}

/**
 * A For each step's blobs: the shown clouds' selected points split into parts within reach of each other (as one
 * cloud: the scans overlap), biggest first, those of min to max points (max 0, left empty: any), at most `most` of
 * them (0, left empty: all; blobs skipped as too big don't count). dropped: the selected points in none of them.
 */
export function blobsOf(clouds: Cloud[], at: Held[], cur: Mask[], p: Params): { blobs: Part[]; dropped: number } {
  const use = clouds.map((_, k) => k).filter(k => !at[k].off);
  let total = 0;
  for (const k of use) total += sel.count(cur[k]);
  const xyz = new Float32Array(3 * total), from = new Int32Array(total), idx = new Uint32Array(total);
  let o = 0;
  for (const k of use) {
    const m = cur[k], P = clouds[k].P;
    for (let i = 0; i < m.length; i++) if (m[i]) { xyz.set(P.subarray(3 * i, 3 * i + 3), 3 * o); from[o] = k; idx[o++] = i; }
  }
  const { part, n } = components(xyz, Math.max(0.005, +p.reach || 0.05), Math.max(1, +p.min || 1));
  const size = new Int32Array(n);
  for (const q of part) if (q >= 0) size[q]++;
  const max = +p.max > 0 ? +p.max : Infinity, most = +p.most > 0 ? +p.most : Infinity, keep = new Int32Array(n).fill(-1);
  let nb = 0;
  for (let q = 0; q < n && nb < most; q++) if (size[q] <= max) keep[q] = nb++;
  const blobs: Part[] = [], fill = new Int32Array(nb);
  for (let q = 0; q < n; q++) if (keep[q] >= 0) blobs.push({ from: new Int32Array(size[q]), idx: new Uint32Array(size[q]) });
  let dropped = 0;
  for (let j = 0; j < total; j++) {
    const b = part[j] >= 0 ? keep[part[j]] : -1;
    if (b < 0) { dropped++; continue; }
    const x = blobs[b], f = fill[b]++;
    x.from[f] = from[j]; x.idx[f] = idx[j];
  }
  return { blobs, dropped };
}

/**
 * The shown clouds' selected points split by a key per point (null: in none), in order of cloud then key, at most
 * `most` of them, each with its cloud and key. dropped: selected points in none of them (no key, or past the most).
 */
function partsBy(clouds: Cloud[], at: Held[], cur: Mask[], most: number, key: (c: Cloud, i: number) => number | null) {
  const all: { k: number; key: number; part: Part }[] = [];
  let dropped = 0;
  clouds.forEach((c, k) => {
    if (at[k].off) return;
    const m = cur[k], here = new Map<number, number[]>();
    for (let i = 0; i < m.length; i++) {
      if (!m[i]) continue;
      const q = key(c, i);
      if (q === null) { dropped++; continue; }
      const l = here.get(q);
      if (l) l.push(i); else here.set(q, [i]);
    }
    for (const q of [...here.keys()].sort((a, b) => a - b)) { const l = here.get(q)!; all.push({ k, key: q, part: { from: new Int32Array(l.length).fill(k), idx: Uint32Array.from(l) } }); }
  });
  const parts = all.slice(0, most);
  for (const x of all.slice(most)) dropped += x.part.idx.length;
  return { parts, dropped };
}

/** A For each step's parts as the run goes over them: each part's points, its variable's value, its name in a message. */
interface Each { parts: Part[]; val: number[]; name: string[]; dropped: number; what: string; none?: string }

/** What a For each step (not a count) goes over in the selection cur, or why it can't. */
function eachOf(clouds: Cloud[], at: Held[], cur: Mask[], p: Params): Each | string {
  const most = +p.most > 0 ? +p.most : Infinity;   // 0: all
  if (p.over === 'frames') {
    // a frame is a scan's: the overlay's points by scan and frame (its frame number the variable)
    if (!clouds.some((c, k) => !at[k].off && c.frame?.some(f => f))) return 'no frame ids in these scans';
    const F = 1 << 24, { parts, dropped } = partsBy(clouds, at, cur, most, (c, i) => (c.frame?.[i] ? (c.si?.[i] ?? 0) * F + c.frame[i] : null));
    const many = new Set(parts.map(x => `${x.k}:${Math.floor(x.key / F)}`)).size > 1;
    const scan = (x: { k: number; key: number }) => (clouds[x.k].si ? `scan ${Math.floor(x.key / F) + 1}` : clouds[x.k].name ?? clouds[x.k].id);
    return { parts: parts.map(x => x.part), val: parts.map(x => x.key % F), name: parts.map(x => `frame ${x.key % F}${many ? ` of ${scan(x)}` : ''}`), dropped, what: 'frame', none: 'no frame ids in the selection' };
  }
  if (p.over === 'scans') {
    const { parts, dropped } = partsBy(clouds, at, cur, most, (c, i) => c.si?.[i] ?? 0);   // the overlay's by scan
    return { parts: parts.map(x => x.part), val: parts.map((_, t) => t + 1), name: parts.map(x => (clouds[x.k].si ? `scan ${x.key + 1}` : clouds[x.k].name ?? clouds[x.k].id)), dropped, what: 'scan' };
  }
  const { blobs, dropped } = blobsOf(clouds, at, cur, p);
  return { parts: blobs, val: blobs.map((_, t) => t + 1), name: blobs.map((_, t) => `blob ${t + 1}`), dropped, what: 'blob', none: 'no blobs that size: nothing selected' };
}

/**
 * Run steps in order from the selection cur, a step at a time: each yields what it did; a Group edit or save yields
 * 'commit' and goes on with what the page passes back; the first step that can't run (or throws) ends it.
 * ∩ with nothing selected stays nothing (the toolbar starts afresh instead). A For each step runs its own steps
 * (the body after it) once a time round, its variable's number put in them, yielding each time round as its step,
 * and at the end everything they left selected.
 */
export function* run(clouds: Cloud[], ctx: Ctx, cur: Mask[], steps: Step[], mem: Mem = { macro: true }, now = () => performance.now()): Generator<Ev, Mask[], Resume> {
  const rounds: Round[] = [], g = span(clouds, ctx, cur, steps, mem, 0, steps.length, new Map(), rounds);
  // inside For each steps the page hears of the steps at most every LIGHT ms (where the run is, what it says) and
  // of the selection and data groups they changed at most every FULL ms (they are the size of the clouds; a time
  // round can take well under a millisecond): held back till then, and always before anything else
  let held: Extract<Ev, { k: 'step' }> | null = null, data: Out['data'], light = -Infinity, full = now();
  const hold = (ev: Extract<Ev, { k: 'step' }>) => {
    const was = held?.out, out: Out = { ...ev.out };
    if (ev.out.data) data = { ...data, ...ev.out.data };
    if (was) {   // what the steps before it found stays (a plane, a measurement) unless it has its own
      for (const k of ['plane', 'dens', 'densRange'] as const) if (out[k] === undefined && was[k] !== undefined) (out as unknown as Record<string, unknown>)[k] = was[k];
      if (!out.changed) Object.assign(out, { sel: was.sel, changed: was.changed, rule: was.rule });
    }
    held = { ...ev, out };
  };
  /** What was held back, as one step (null: nothing). */
  const flush = (): Ev | null => {
    if (!held) return null;
    const ev: Ev = data ? { ...held, out: { ...held.out, data } } : held;
    held = null; data = undefined; full = now();
    return ev;
  };
  for (let it = g.next(); ; ) {
    if (it.done) { const f = flush(); if (f) yield f; return it.value[0]; }
    const ev: Ev = rounds.length ? { ...it.value, at: whereIn(rounds, it.value.i) } : it.value;
    const inside = rounds.some(r => !r.done);
    if (ev.k === 'step' && inside) {
      hold(ev);
      const t = now();
      if (t - full >= FULL) { yield flush()!; light = t; }
      else if (t - light >= LIGHT) { light = t; yield { ...ev, out: { ok: true, sel: [], changed: false, say: ev.out.say, warn: ev.out.warn } }; }
      it = g.next(undefined);
      continue;
    }
    if (ev.k === 'step' && held) hold(ev);   // a For each's last: everything with it
    const f = flush();
    if (f) yield f;
    it = g.next(ev.k === 'step' && f ? undefined : yield ev);
  }
}

/** Inside For each steps, how often the page hears how the run is going, and gets its selection and data groups (ms). */
const LIGHT = 100, FULL = 1000;

/**
 * run over steps from to to (a For each step's body, or them all): the selection after, and false when one couldn't
 * (the run ends). loops: each For each under way's number (by its loop); rounds: their time rounds, outer first.
 */
function* span(clouds: Cloud[], ctx: Ctx, cur: Mask[], steps: Step[], mem: Mem, from: number, to: number, loops: Map<number, number>, rounds: Round[]): Generator<Ev, [Mask[], boolean], Resume> {
  for (let i = from; i < to; i++) {
    let s = steps[i];
    const late = loopsIn(s);
    if (late) {
      try { s = bindLoop(s, loops); } catch (e) { yield { k: 'fail', i, why: (e as Error).message }; return [cur, false]; }
      const keys = srcKeys([s]).filter(k => !ctx.src[k]);
      if (keys.length) {   // the page works out the sources it names now it knows them
        const r = yield { k: 'src', i, keys };
        ctx.src = { ...ctx.src, ...r?.ctx?.src };
      }
    }
    if (s.t === 'blobs') {
      const body = s.body ?? 0, at = ctx.at, lp = s.loop ?? -1, over = s.p.over ?? 'blobs';
      if (over === 'count') {   // n times, each from what the one before left
        const n = Math.max(0, Math.round(+s.p.n || 0));
        const round: Round = { i, body, b: 0, n, text: '' };
        if (body && n) rounds.push(round);
        for (let t = 1; t <= (body ? n : 0); t++) {
          loops.set(lp, t);
          Object.assign(round, { b: t - 1, text: `${t} of ${n}` });
          yield { k: 'step', i, out: { ok: true, sel: cur, changed: false, say: `<${s.p.var}> = ${t} of ${n}` } };
          const [got, ok] = yield* span(clouds, ctx, cur, steps, mem, i + 1, i + 1 + body, loops, rounds);
          cur = got;
          if (!ok) return [got, false];
        }
        Object.assign(round, { b: n, done: true });
        loops.delete(lp);
        yield { k: 'step', i, out: { ok: true, sel: cur, changed: false, say: `ran ${n} time${n === 1 ? '' : 's'}` } };
        if (body && n) rounds.pop();
        i += body;
        continue;
      }
      if (!clouds.some((_, k) => !at[k].off && cur[k].some(x => x))) {
        const parked = clouds.reduce((a, _, k) => a + (at[k].off ? sel.count(cur[k]) : 0), 0);
        yield { k: 'fail', i, why: parked ? `select some points first: those selected (${parked.toLocaleString()}) are all in scans unticked in Scans` : 'select some points first' };
        return [cur, false];
      }
      const each = eachOf(clouds, at, cur, s.p);
      if (typeof each === 'string') { yield { k: 'fail', i, why: each }; return [cur, false]; }
      const { parts, val, name, dropped, what } = each, n = parts.length;
      const all = cur.map((m, k) => (at[k].off ? m.slice() : new Uint8Array(m.length)));   // parked clouds keep theirs
      if (!body) for (const x of parts) maskOf(x, cur, all);   // nothing inside: just the parts (blobs that size, …)
      const round: Round = { i, body, b: 0, n, text: '' };
      if (body && n) rounds.push(round);
      const outer = mem.part;
      for (let b = 0; b < (body ? n : 0); b++) {
        const one = maskOf(parts[b], cur).map((m, k) => (at[k].off ? cur[k] : m)), idx = byCloud(parts[b], clouds.length);
        loops.set(lp, val[b]);
        Object.assign(round, { b, text: what === 'blob' ? `blob ${b + 1} of ${n}` : `${name[b]} (${b + 1} of ${n})` });
        const say = `${what} ${b + 1} of ${n}${what === 'blob' ? '' : ` (${name[b]})`}: ${parts[b].idx.length.toLocaleString()} points, <${s.p.var}> = ${val[b]}`;
        yield { k: 'step', i, out: { ok: true, sel: one, changed: true, say } };
        mem.part = { sel: one, idx };
        const [got, ok] = yield* span(clouds, ctx, one, steps, mem, i + 1, i + 1 + body, loops, rounds);
        mem.part = outer;
        if (!ok) return [got, false];
        if (got === one) maskOf(parts[b], cur, all);   // left as it was: just its points
        else got.forEach((m, k) => { if (!at[k].off) { const a = all[k]; for (let v = 0; v < m.length; v++) a[v] |= m[v]; } });
      }
      Object.assign(round, { b: n, done: true });
      loops.delete(lp);
      cur = all;
      const why = over === 'blobs' ? 'too small or big, or past the most' : over === 'frames' ? 'no frame id, or past the most' : 'past the most';
      const say = n ? `${n} ${what}${n === 1 ? '' : 's'}${dropped ? `, ${dropped.toLocaleString()} points in none (${why})` : ''}` : each.none ?? 'nothing selected';
      const rule = over === 'blobs' ? { t: 'blobs', reach: s.p.reach, min: s.p.min, max: s.p.max } : { t: 'each', over };
      yield { k: 'step', i, out: { ok: true, sel: cur, changed: true, rule: { wrap: rule }, say, warn: !n } };
      if (body && n) rounds.pop();
      i += body;
      continue;
    }
    const bound = late ? { s } : {};
    if (s.t === 'groupEdit' || s.t === 'groupSave' || s.t === 'plane' || s.t === 'weight' || s.t === 'weightBlur') {
      const r = yield { k: 'commit', i, ...bound };
      if (r?.ctx) Object.assign(ctx, r.ctx);
      continue;
    }
    if (PHOTO.has(s.t)) {   // the page does it (photos, its pools); a step that selects then picks from what it found
      const r = yield { k: 'photo', i, ...bound };
      if (r?.ctx) Object.assign(ctx, r.ctx);
      if (r?.fail) { yield { k: 'fail', i, why: r.fail }; return [cur, false]; }
      if (!PHOTO_SEL.has(s.t)) { yield { k: 'step', i, out: { ok: true, sel: cur, changed: false, say: r?.say, warn: r?.warn } }; continue; }
      let out: Out;
      try { out = evalStep(clouds, cur, ctx, s, mem); } catch (e) { yield { k: 'fail', i, why: (e as Error).message }; return [cur, false]; }
      if (!out.ok) { yield { k: 'fail', i, why: out.say ?? 'it couldn\'t' }; return [cur, false]; }
      cur = out.sel;
      yield { k: 'step', i, out: { ...out, say: [r?.say, out.say].filter(Boolean).join('; ') || undefined, warn: r?.warn || out.warn } };
      continue;
    }
    if (s.mode === 'inter' && !anySel(cur)) { yield { k: 'step', i, out: { ok: true, sel: cur, changed: false } }; continue; }
    const dr = dataDensR(s);
    if (s.t === 'density' || (s.t === 'tune' && s.p.density) || dr !== null) {
      const r = dr ?? +s.p.r, key = densKey(ctx, r);
      if (ctx.dens?.key !== key && mem.dens?.key !== key) {
        const got = yield { k: 'measure', i, r };
        if (got?.ctx?.dens?.key === key) ctx.dens = got.ctx.dens;
      }
    }
    if (s.t === 'tune') {   // a round at a time, the page told how it's going
      const list = (l: 'req' | 'deny') => { const x = ctx.src[tuneKey(i, l)]; return x && 'm' in x ? x.m : clouds.map(() => null); };
      let out: Out;
      try {
        const g = tunePasses(clouds, cur, ctx, s, list('req'), list('deny'), mem);
        for (;;) {
          const it = g.next();
          if (it.done) { out = it.value; break; }
          yield { k: 'tune', i, stat: it.value.stat, out: it.value.out };
        }
      } catch (e) { yield { k: 'fail', i, why: (e as Error).message }; return [cur, false]; }
      mem.finish = false;   // a Finish is for the one under way
      cur = out.sel;
      yield { k: 'step', i, out };
      continue;
    }
    let out: Out;
    try { out = evalStep(clouds, cur, ctx, s, mem); } catch (e) { yield { k: 'fail', i, why: (e as Error).message }; return [cur, false]; }
    if (!out.ok) { yield { k: 'fail', i, why: out.say ?? 'it couldn\'t' }; return [cur, false]; }
    cur = out.sel;
    if (out.plane) ctx.plane = out.plane;
    if (out.data) {   // the data groups as the step left them, for the steps after it
      const data = { ...ctx.data };
      for (const [n, g] of Object.entries(out.data)) if (g) data[n] = g; else delete data[n];
      ctx.data = data;
    }
    yield { k: 'step', i, out };
  }
  return [cur, true];
}
